import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function hexToRgb(hex) {
  const raw = String(hex || '').replace('#', '').trim();
  if (raw.length !== 6) return [17, 17, 17];
  const n = parseInt(raw, 16);
  if (Number.isNaN(n)) return [17, 17, 17];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Strip HTML tags and entities, map Unicode symbols to standard ASCII equivalents for jsPDF font compatibility. */
export function sanitizeExportCellText(value) {
  if (value == null) return '';
  let str = String(value);

  // 1. Decode HTML entities and tags
  str = str
    .replace(/&amp;(nbsp|amp|lt|gt|quot|#39);/gi, '&$1;')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\r\n/g, '\n');

  // 2. Map Unicode symbols cleanly to ASCII without extra prefix tags
  str = str
    .replace(/[\u2611\u2612\u2713\u2714\u2610]/g, '')   // Strip checkbox symbols cleanly
    .replace(/\u2265/g, '>=')
    .replace(/\u2264/g, '<=')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u00A0\u1680\u180E\u2000-\u200B\u202F\u205F\u3000\uFEFF]/g, ' ');

  // 3. Remove non-printable non-ASCII characters outside WinAnsi range (0x20 - 0x7E + newlines + tabs)
  str = str.replace(/[^\x0A\x0D\x20-\x7E]/g, '');

  return str.trim();
}

function cellText(cell) {
  if (cell == null) return '';
  if (typeof cell === 'object') {
    return sanitizeExportCellText(cell.text ?? cell.csv ?? '');
  }
  return sanitizeExportCellText(cell);
}

/**
 * Export a lookup/follow-up style table to PDF.
 * Uses jspdf-autotable so rows are never split across page boundaries unless tall.
 *
 * @param {Object} options
 * @param {string} options.filename
 * @param {string} options.title
 * @param {string[]} [options.metaLines]
 * @param {string[]} options.headers
 * @param {Array<Array<{text?: string, color?: string}|string|number>>} options.rows
 * @param {string} [options.headerBg="#0077b6"]
 * @param {Object} [options.columnStyles] jspdf-autotable columnStyles by index
 * @param {number} [options.fontSize=7.5]
 * @param {number} [options.marginX=28]
 */
export function exportColoredTablePdf({
  filename,
  title,
  metaLines = [],
  headers = [],
  rows = [],
  headerBg = '#0077b6',
  columnStyles,
  fontSize = 7.5,
  marginX = 28
}) {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'letter'
  });

  let y = 28;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...hexToRgb(headerBg));
  doc.text(String(title || 'Report'), marginX, y);
  y += 14;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(85, 85, 85);
  metaLines.forEach((line) => {
    const text = String(line || '');
    if (!text) return;
    const wrapped = doc.splitTextToSize(text, doc.internal.pageSize.getWidth() - marginX * 2);
    doc.text(wrapped, marginX, y);
    y += wrapped.length * 10;
  });
  y += 6;

  const body = rows.map((row) => (Array.isArray(row) ? row.map(cellText) : []));
  const headRgb = hexToRgb(headerBg);
  const pageWidth = doc.internal.pageSize.getWidth();
  const usableWidth = pageWidth - marginX * 2;

  autoTable(doc, {
    startY: y,
    head: [headers],
    body,
    theme: 'grid',
    tableWidth: usableWidth,
    styles: {
      fontSize,
      cellPadding: { top: 4, right: 4, bottom: 4, left: 4 },
      overflow: 'linebreak',
      valign: 'top',
      halign: 'left',
      textColor: [17, 17, 17],
      lineColor: [208, 215, 222],
      lineWidth: 0.4,
      minCellHeight: 14
    },
    headStyles: {
      fillColor: headRgb,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: Math.max(7, fontSize - 0.5),
      halign: 'left',
      valign: 'middle',
      overflow: 'linebreak',
      cellPadding: { top: 5, right: 4, bottom: 5, left: 4 }
    },
    alternateRowStyles: {
      fillColor: [245, 247, 250]
    },
    columnStyles: columnStyles || undefined,
    rowPageBreak: 'avoid',
    showHead: 'everyPage',
    margin: { left: marginX, right: marginX, top: 28, bottom: 36 },
    didParseCell: (data) => {
      if (data.section !== 'body') return;
      const src = rows[data.row.index]?.[data.column.index];
      if (src && typeof src === 'object' && src.color) {
        data.cell.styles.textColor = hexToRgb(src.color);
        data.cell.styles.fontStyle = 'bold';
      }
    }
  });

  doc.save(filename);
}
